class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));

    this.setHeaderCartIconAccessibility();
    this.debouncedUpdateCart = this.debounce(this.updateCartDrawer, 100);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  async loadCartContents() {
    try {
      const response = await fetch('?section_id=cart-drawer');
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const cartDrawerContent = doc.querySelector('.drawer__inner');
      
      if (cartDrawerContent) {
        const currentDrawer = this.querySelector('.drawer__inner');
        if (currentDrawer) {
          currentDrawer.innerHTML = cartDrawerContent.innerHTML;
        }
        this.open();
      }

      // Update cart count
      const cartCount = doc.querySelector('.cart-count-bubble span[aria-hidden="true"]');
      const headerCartCount = document.querySelector('.cart-count-bubble span[aria-hidden="true"]');
      if (cartCount && headerCartCount) {
        headerCartCount.textContent = cartCount.textContent;
      }

    } catch (error) {
      console.error('Error loading cart contents:', error);
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const drawer = this.querySelector('.drawer__inner');
    if (!drawer) return;

    requestAnimationFrame(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.querySelector('.drawer__inner');
      if (containerToTrapFocusOn) {
        const focusElement = containerToTrapFocusOn.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (focusElement) {
          focusElement.focus();
        }
      }

      if (typeof updateCartDrawer === 'function') {
        updateCartDrawer();
      }
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.open();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const sectionElement = parsedHTML.querySelector(selector);

    if (!sectionElement) return '';

    return sectionElement.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  debounce(func, wait) {
    let timeout;
    return function(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  async updateCartDrawer() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      if (!cart) return;

      // Update cart count
      const cartCountBubble = document.querySelector('.cart-count-bubble span[aria-hidden="true"]');
      if (cartCountBubble) {
        cartCountBubble.textContent = cart.item_count;
      }

      // Filter items
      const homeTryOnItems = cart.items.filter(item => item.properties?.home_try_on);
      const regularItems = cart.items.filter(item => !item.properties?.home_try_on);
      
      // Update home try-on section
      this.updateHomeTryOnSection(homeTryOnItems);
      
      // Update upsell section
      this.updateUpsellSection(regularItems);

    } catch (error) {
      console.error('Error updating cart drawer:', error);
    }
  }

  updateHomeTryOnSection(homeTryOnItems) {
    const homeTryOnSection = document.querySelector('.home-try-on-section');
    const checkoutButton = document.querySelector('#CartDrawer-Checkout');
    
    if (homeTryOnSection && checkoutButton) {
      homeTryOnSection.style.display = homeTryOnItems.length > 0 ? 'block' : 'none';
      
      const itemCount = homeTryOnItems.length;
      let warningMessage = '';
      let disableCheckout = false;

      if (itemCount > 0 && itemCount < 4) {
        warningMessage = `Please add at least 4 items for Home Try On. You currently have ${itemCount} items.`;
        disableCheckout = true;
      } else if (itemCount > 6) {
        warningMessage = 'Please remove some items. Maximum limit is 6 items for Home Try On.';
        disableCheckout = true;
      }

      // Update counter display
      const counterDiv = homeTryOnSection.querySelector('.home-try-on-counter') || 
                        this.createCounterDiv(homeTryOnSection);
      
      counterDiv.innerHTML = `
        <p>Home Try On Items: ${itemCount}/6</p>
        ${warningMessage ? `<p class="warning">${warningMessage}</p>` : ''}
      `;

      // Update checkout button
      this.updateCheckoutButton(checkoutButton, disableCheckout, homeTryOnItems.length);
    }
  }

  createCounterDiv(homeTryOnSection) {
    const counterDiv = document.createElement('div');
    counterDiv.className = 'home-try-on-counter';
    homeTryOnSection.insertBefore(counterDiv, homeTryOnSection.firstChild);
    return counterDiv;
  }

  updateCheckoutButton(checkoutButton, disableCheckout, itemCount) {
    if (disableCheckout) {
      checkoutButton.setAttribute('disabled', 'disabled');
      checkoutButton.style.opacity = '0.5';
      checkoutButton.style.cursor = 'not-allowed';
    } else if (itemCount >= 4 && itemCount <= 6) {
      checkoutButton.removeAttribute('disabled');
      checkoutButton.style.opacity = '1';
      checkoutButton.style.cursor = 'pointer';
    }

    checkoutButton.textContent = itemCount > 0 ? 
      (disableCheckout ? 'Home Try On Requirements Not Met' : 'Checkout') : 
      'Checkout';
  }

  async updateUpsellSection(regularItems) {
    console.log(regularItems,"regularItems");
    
    if (regularItems.length > 0) {
      const allUpsellProductIds = await Promise.all(
        regularItems.map(item => fetchUpsellProductIds(item.handle))
      ).then(ids => [...new Set(ids.flat())]);

      if (allUpsellProductIds.length > 0) {
        await fetchUpsellProducts(allUpsellProductIds, regularItems);
        const upsellSection = document.querySelector('.cart-upsell-section');
        if (upsellSection) {
          upsellSection.style.display = 'block';
        }
      }
    } else {
      const upsellSection = document.querySelector('.cart-upsell-section');
      if (upsellSection) {
        upsellSection.style.display = 'none';
      }
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

function fetchUpsellProductIds(productHandle) {
  if (!productHandle || typeof productHandle !== 'string') return Promise.resolve([]);

  return fetch('/api/2023-07/graphql.json', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': '06afe168ab86f13c34109a6bd2909dee',
    },
    body: JSON.stringify({
      query: `
        query getProductMetafields($handle: String!) {
          product(handle: $handle) {
            metafield(namespace: "custom", key: "upsells") {
              value
            }
          }
        }
      `,
      variables: { handle: productHandle },
    }),
  })
    .then((response) => {
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);
      return response.json();
    })
    .then((data) => {
      const metafield = data.data.product?.metafield;
      if (!metafield?.value) return [];
      try {
        return JSON.parse(metafield.value);
      } catch (error) {
        return [];
      }
    })
    .catch((error) => {
      return [];
    });
}

function fetchUpsellProducts(upsellProductIds, cartItems) {
  const upsellContainer = document.querySelector(".upsell-products");
  if (!upsellContainer) return;
  upsellContainer.innerHTML = "";
  if (upsellProductIds.length === 0) return;

  const query = `
    query getUpsellProducts($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on Product {
          id
          title
          handle
          featuredImage {
            url
          }
          variants(first: 10) {
            edges {
              node {
                id
                title
                availableForSale
                priceV2 {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      }
    }
  `;

  fetch("/api/2023-07/graphql.json", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": "06afe168ab86f13c34109a6bd2909dee",
    },
    body: JSON.stringify({
      query,
      variables: { ids: upsellProductIds },
    }),
  })
    .then((response) => {
      console.log("==================================Hello=============")
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);
      return response.json();    })
    .then((data) => {
      if (!data.data?.nodes) return;

      // Filter out null values from the nodes array
      const filteredNodes = data.data.nodes.filter((node) => node !== null);

      const filteredUpsellProducts = filteredNodes.filter((product) => {
        const productId = product.id;
        return !cartItems.some(
          (item) => item.product_id.toString() === productId.split("/").pop()
        );
      });

      filteredUpsellProducts.forEach((product) => {
        if (!product) return;
        const availableVariants = product.variants.edges.filter(
          (variant) => variant.node.availableForSale
        );

        const sizeOptions = availableVariants
          .map(
            (variant) => `
              <option value="${variant.node.id}">
                ${variant.node.title} - ${variant.node.priceV2.amount} ${variant.node.priceV2.currencyCode}
              </option>
            `
          )
          .join("");
        if (availableVariants.length === 0) return;

        const upsellProductHTML = `
          <div class="upsell-product" data-product-id="${product.id}">
            <a href="/products/${product.handle}">
              <img src="${product.featuredImage.url}" alt="${product.title}" />
              <h3>${product.title}</h3>
            </a>
            <select class="size-selector">
              ${sizeOptions}
            </select>
            <button type="button" class="add-to-cart-btn" data-product-id="${product.id}">
              Add to Cart
            </button>
          </div>
        `;
        upsellContainer.innerHTML += upsellProductHTML;
      });
      attachAddToCartListeners();
    })
    .catch((error) => {
      console.error("Error fetching upsell products:", error);
    });
}


function attachAddToCartListeners() {
  const addToCartButtons = document.querySelectorAll('.add-to-cart-btn');
  addToCartButtons.forEach((button) => {
    button.addEventListener('click', (event) => {
      const productId = event.target.getAttribute('data-product-id');
      const sizeSelector = event.target.closest('.upsell-product').querySelector('.size-selector');
      const selectedVariantId = sizeSelector.value;

      if (selectedVariantId) {
        addProductToCart(selectedVariantId);
      }
    });
  });
}

function addProductToCart(variantId) {
  const numericVariantId = variantId.split('/').pop();

  fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      items: [
        {
          id: numericVariantId,
          quantity: 1,
        },
      ],
    }),
  })
    .then((response) => {
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      return response.json();
    })
    .then((data) => {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        cartDrawer.loadCartContents();
      }
      updateCartDrawer();
    })
    .catch((error) => {
      console.error('Error adding product to cart:', error);
    });
}

// Home Try On

function addToHomeTryOn(variantId) {
  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      items: [{
        id: variantId,
        quantity: 1,
        properties: { home_try_on: true }
      }]
    })
  })
  .then(() => {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.loadCartContents();
    }
  });
}

function debounce(func, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

const updateCartDrawer = debounce(async function() {
  try {
    const response = await fetch('/cart.js');
    const cart = await response.json();
    
    if (!cart) return;

    const cartCountBubble = document.querySelector('.cart-count-bubble span[aria-hidden="true"]');
    if (cartCountBubble) {
      cartCountBubble.textContent = cart.item_count;
    }

    const homeTryOnItems = cart.items.filter(item => item.properties?.home_try_on);
    const regularItems = cart.items.filter(item => !item.properties?.home_try_on);
    const homeTryOnSection = document.querySelector('.home-try-on-section');
    
    // Get checkout button
    const checkoutButton = document.querySelector('#CartDrawer-Checkout');
    
    if (homeTryOnSection && checkoutButton) {
      homeTryOnSection.style.display = homeTryOnItems.length > 0 ? 'block' : 'none';

      // Add or update the counter display
      let counterDiv = homeTryOnSection.querySelector('.home-try-on-counter');
      if (!counterDiv) {
        counterDiv = document.createElement('div');
        counterDiv.className = 'home-try-on-counter';
        homeTryOnSection.insertBefore(counterDiv, homeTryOnSection.firstChild);
      }
      
      const itemCount = homeTryOnItems.length;
      let warningMessage = '';
      let disableCheckout = false;

      // Determine warning message and checkout state
      if (itemCount > 0 && itemCount < 4) {
        warningMessage = `Please add at least 4 items for Home Try On. You currently have ${itemCount} items.`;
        disableCheckout = true;
      } else if (itemCount > 6) {
        warningMessage = 'Please remove some items. Maximum limit is 6 items for Home Try On.';
        disableCheckout = true;
      }

      // Update counter display
      counterDiv.innerHTML = `
        <p>Home Try On Items: ${itemCount}/6</p>
        ${warningMessage ? `<p class="warning">${warningMessage}</p>` : ''}
      `;

      // Update checkout button state
      if (disableCheckout) {
        checkoutButton.setAttribute('disabled', 'disabled');
        checkoutButton.style.opacity = '0.5';
        checkoutButton.style.cursor = 'not-allowed';
      } else if (itemCount >= 4 && itemCount <= 6) {
        checkoutButton.removeAttribute('disabled');
        checkoutButton.style.opacity = '1';
        checkoutButton.style.cursor = 'pointer';
      }

      // Update checkout button text
      if (homeTryOnItems.length > 0) {
        checkoutButton.textContent = disableCheckout ? 'Home Try On Requirements Not Met' : 'Checkout';
      }
    }

    // Update upsell section
    if (cart.items.length > 0) {
      const regularItems = cart.items.filter(item => !item.properties?.home_try_on);
      if (regularItems.length > 0) {
        const allUpsellProductIds = await Promise.all(
          regularItems.map(item => fetchUpsellProductIds(item.handle))
        ).then(ids => [...new Set(ids.flat())]);

        if (allUpsellProductIds.length > 0) {
          await fetchUpsellProducts(allUpsellProductIds, regularItems);
          const upsellSection = document.querySelector('.cart-upsell-section');
          if (upsellSection) {
            upsellSection.style.display = 'block';
          }
        }
      } else {
        const upsellSection = document.querySelector('.cart-upsell-section');
        if (upsellSection) {
          upsellSection.style.display = 'none';
        }
      }
    }

  } catch (error) {
    console.error('Error updating cart drawer:', error);
  }
}, 100);

document.addEventListener("DOMContentLoaded", function () {
  let lastCartItemCount = 0;

  document.body.addEventListener("click", function (event) {
    const button = event.target.closest(".add-to-cart");
    if (!button) return;

    event.preventDefault();
    const productHandle = button.dataset.productHandle;

    if (!productHandle) return;

    handleAddToCartClick(productHandle);
  });

  async function handleAddToCartClick(productHandle) {
    try {
      const product = await fetchProductDetails(productHandle);
      const availableVariants = product.variants.filter((v) => v.available);

      if (availableVariants.length === 0) {
        alert("Out of stock");
        return;
      }

      if (availableVariants.length === 1) {
        await addProductToCart(availableVariants[0].id);
      } else {
        const variantId = await showSizeSelector(availableVariants);
        if (variantId) await addProductToCart(variantId);
      }
    } catch (error) {
      alert("No More Products Available of this Size");
    }
  }

  async function fetchProductDetails(handle) {
    const response = await fetch(`/products/${handle}.js`);
    if (!response.ok) throw new Error("Product not found");
    return response.json();
  }

  function showSizeSelector(variants) {
    return new Promise((resolve) => {
      const modal = document.createElement("div");
      modal.className = "size-selector-modal";
      modal.innerHTML = `
        <div class="modal-content">
          <h2>Choose your Size</h2>
          <select id="size-selector">
            ${variants.map((v) => `<option value="${v.id}">${v.title}</option>`).join("")}
          </select>
          <button id="confirm-size">Add to Cart</button>
          <button id="cancel-size">Cancel</button>
        </div>
      `;

      document.body.appendChild(modal);

      modal.querySelector("#cancel-size").addEventListener("click", () => {
        modal.remove();
        resolve(null);
      });

      modal.querySelector("#confirm-size").addEventListener("click", () => {
        const variantId = modal.querySelector("#size-selector").value;
        modal.remove();
        resolve(variantId);
      });
    });
  }

  async function addProductToCart(variantId) {
    try {
      const addResponse = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
        }),
      });

      if (!addResponse.ok) throw new Error("Add to cart failed");

      const cartData = await fetch("/cart.js").then((r) => r.json());
      lastCartItemCount = cartData.item_count;

      updateCartUI(cartData);

      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer) {
        cartDrawer.loadCartContents().then(() => cartDrawer.open());
      }
    } catch (error) {
      alert("Error updating cart");
    }
  }

  function updateCartUI(cartData) {
    const cartIcon = document.querySelector("#cart-icon-bubble");
    const cartCountBubble = document.querySelector(".cart-count-bubble span[aria-hidden='true']");

    if (!cartIcon || !cartCountBubble) {
      recreateCartIcon(cartData.item_count);
      return;
    }

    cartCountBubble.textContent = cartData.item_count || 0;
    cartCountBubble.parentElement.style.display = cartData.item_count > 0 ? "block" : "none";
    cartIcon.style.display = cartData.item_count > 0 ? "flex" : "none";

    document.dispatchEvent(
      new CustomEvent("cart:updated", { detail: { cart: cartData } })
    );
  }

  function recreateCartIcon(itemCount) {
    const headerCartContainer = document.querySelector(".header__cart");
    if (!headerCartContainer) return;

    const cartIcon = document.createElement("a");
    cartIcon.className = "header__icon header__icon--cart link link--text focus-inset";
    cartIcon.id = "cart-icon-bubble";
    cartIcon.setAttribute("role", "button");
    cartIcon.setAttribute("aria-haspopup", "dialog");

    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.className = "icon icon-cart";
    svg.setAttribute("width", "22");
    svg.setAttribute("height", "23");
    svg.setAttribute("viewBox", "0 0 22 23");
    svg.setAttribute("fill", "none");
    svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");

    const path1 = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path1.setAttribute(
      "d",
      "M17.9437 6.6875H4.05622C3.88705 6.68829 3.72396 6.75067 3.59744 6.86296C3.47091 6.97525 3.3896 7.12978 3.36872 7.29766L2.14841 18.2977C2.13756 18.3935 2.14699 18.4906 2.1761 18.5825C2.20521 18.6745 2.25335 18.7593 2.31738 18.8314C2.38141 18.9035 2.4599 18.9614 2.54776 19.0012C2.63561 19.041 2.73086 19.0619 2.82732 19.0625H19.1726C19.2691 19.0619 19.3643 19.041 19.4522 19.0012C19.54 18.9614 19.6185 18.9035 19.6826 18.8314C19.7466 18.7593 19.7947 18.6745 19.8238 18.5825C19.853 18.4906 19.8624 18.3935 19.8515 18.2977L18.6312 7.29766C18.6103 7.12978 18.529 6.97525 18.4025 6.86296C18.276 6.75067 18.1129 6.68829 17.9437 6.6875Z"
    );
    path1.setAttribute("stroke", "currentColor");
    path1.setAttribute("stroke-width", "1.5");
    path1.setAttribute("stroke-linecap", "round");
    path1.setAttribute("stroke-linejoin", "round");

    const path2 = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path2.setAttribute(
      "d",
      "M6.875 6C6.875 4.90598 7.3096 3.85677 8.08318 3.08318C8.85677 2.3096 9.90598 1.875 11 1.875C12.094 1.875 13.1432 2.3096 13.9168 3.08318C14.6904 3.85677 15.125 4.90598 15.125 6"
    );
    path2.setAttribute("stroke", "currentColor");
    path2.setAttribute("stroke-width", "1.5");
    path2.setAttribute("stroke-linecap", "round");
    path2.setAttribute("stroke-linejoin", "round");

    svg.appendChild(path1);
    svg.appendChild(path2);
    cartIcon.appendChild(svg);

    const visuallyHiddenLabel = document.createElement("span");
    visuallyHiddenLabel.className = "visually-hidden";
    visuallyHiddenLabel.textContent = "Cart";
    cartIcon.appendChild(visuallyHiddenLabel);

    const cartCountBubble = document.createElement("div");
    cartCountBubble.className = "cart-count-bubble";
    const cartCountSpan = document.createElement("span");
    cartCountSpan.setAttribute("aria-hidden", "true");
    cartCountSpan.textContent = itemCount || 0;
    cartCountBubble.appendChild(cartCountSpan);
    cartCountBubble.innerHTML += `<span class="visually-hidden">${itemCount || 0} items</span>`;
    cartIcon.appendChild(cartCountBubble);

    headerCartContainer.innerHTML = "";
    headerCartContainer.appendChild(cartIcon);

    cartIcon.addEventListener("click", function (event) {
      event.preventDefault();
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer) {
        cartDrawer.loadCartContents().then(() => cartDrawer.open());
      }
    });
  }

  setInterval(async () => {
    const cartIcon = document.querySelector("#cart-icon-bubble");
    const cartCountBubble = document.querySelector(".cart-count-bubble span[aria-hidden='true']");

    if (!cartIcon || !cartCountBubble) {
      const cartData = await fetch("/cart.js").then((r) => r.json());
      recreateCartIcon(cartData.item_count);
    } else {
      const cartData = await fetch("/cart.js").then((r) => r.json());
      cartCountBubble.textContent = cartData.item_count || 0;
      cartCountBubble.parentElement.style.display = "block";
      cartIcon.style.display = "flex"; // Always keep it visible

    }
  }, 2000); // Check every 2 seconds

  document.addEventListener("cart:updated", async (event) => {
    const cartData = event.detail.cart;
    updateCartUI(cartData);
  });

  document.addEventListener("cart:cleared", async () => {
    const cartData = await fetch("/cart.js").then((r) => r.json());
    updateCartUI(cartData);
  });
});

document.addEventListener('click', async function(event) {
  if (!event.target.matches('.home-try-on-button')) return;
  
  event.preventDefault();
  
  let selectedVariantId = null;
  
  const form = event.target.closest('form');
  if (form) {
    const variantInput = form.querySelector('input[name="id"][type="radio"]:checked, select[name="id"], input[name="id"][type="hidden"]');
    if (variantInput) {
      selectedVariantId = variantInput.value;
    }
  }
  
  if (!selectedVariantId) {
    selectedVariantId = event.target.dataset.variantId;
  }

  if (!selectedVariantId) {
    console.error('No variant selected');
    return;
  }

  try {
    // First, get current cart contents
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();
    
    // Check if this variant is already in the cart as a home try on item
    const isVariantInCart = cart.items.some(item => 
      item.variant_id === parseInt(selectedVariantId) && 
      item.properties && 
      item.properties.home_try_on === true
    );

    if (isVariantInCart) {
      alert('This size is already in your Home Try On cart.');
      return;
    }

    // Count total home try on items
    const homeTryOnItemsCount = cart.items.filter(item => 
      item.properties && 
      item.properties.home_try_on === true
    ).length;

    // Check minimum and maximum limits
    if (homeTryOnItemsCount >= 6) {
      alert('You can only add up to 6 items for Home Try On.');
      return;
    }

    // If checks pass, add to cart
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        items: [{
          id: parseInt(selectedVariantId),
          quantity: 1,
          properties: { 'home_try_on': true }
        }]
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();

    // // Check if minimum items requirement is met
    // if (homeTryOnItemsCount < 3) {
    //   alert(`Please add at least 4 items for Home Try On. You currently have ${homeTryOnItemsCount + 1} items.`);
    // }

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      await cartDrawer.loadCartContents();
    }

  } catch (error) {
    console.error('Error:', error);
    alert('Could not add item to cart. Please try again.');
  }
});
